import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import SocialIcons from "./SocialIcons";
import LanguageSwitcher from "./LanguageSwitcher";
import { useI18n } from "../../lib/i18n";

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { t } = useI18n();

    const links = [
        { label: t.nav.industries, href: "#industries", key: "industries" },
        { label: t.nav.services, href: "#services", key: "services" },
        { label: t.nav.pricing, href: "#pricing", key: "pricing" },
        { label: t.nav.work, href: "#portfolio", key: "work" },
        { label: t.nav.contact, href: "#contact", key: "contact" },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.header
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className={`fixed top-0 left-0 right-0 z-50 ${scrolled ? "glass" : ""}`}
            data-testid="site-nav"
        >
            <div className="container-x flex items-center justify-between py-5 gap-4">
                <a
                    href="#top"
                    className="flex items-center gap-2 group"
                    data-testid="nav-logo"
                >
                    <span
                        className="inline-flex items-center justify-center text-white text-[22px] leading-none group-hover:scale-110 transition-transform"
                        style={{ fontFamily: "'Segoe UI Symbol', 'Apple Symbols', 'Noto Sans Symbols 2', sans-serif" }}
                        aria-hidden="true"
                    >
                        ♟
                    </span>
                    <span className="font-heading text-[15px] tracking-[0.24em] font-medium">
                        FTAKLAKIS MEDIA
                    </span>
                </a>

                <nav className="hidden md:flex items-center gap-9">
                    {links.map((l) => (
                        <a
                            key={l.key}
                            href={l.href}
                            className="font-body text-[13px] text-white/70 hover:text-white transition-colors relative group"
                            data-testid={`nav-link-${l.key}`}
                        >
                            {l.label}
                            <span className="absolute left-0 -bottom-1 w-0 h-[1px] bg-[#E55B13] group-hover:w-full transition-all duration-500" />
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <LanguageSwitcher />
                    <a
                        href="#contact"
                        className="hidden md:inline-block btn-ghost !py-2.5 !px-5 !text-[13px]"
                        data-testid="nav-cta"
                    >
                        {t.nav.letsTalk}
                    </a>
                    <div className="hidden lg:flex items-center">
                        <SocialIcons testidPrefix="nav-social" />
                    </div>
                    <button
                        className="md:hidden text-white p-2"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="nav-mobile-toggle"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="md:hidden glass overflow-hidden"
                    >
                        <div className="container-x py-6 flex flex-col gap-4">
                            {links.map((l) => (
                                <a
                                    key={l.key}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    className="font-heading text-2xl text-white/90"
                                    data-testid={`nav-mobile-link-${l.key}`}
                                >
                                    {l.label}
                                </a>
                            ))}
                            <div className="mt-4 pt-4 border-t border-white/[0.06] flex items-center justify-between gap-4">
                                <SocialIcons
                                    size="lg"
                                    testidPrefix="nav-mobile-social"
                                />
                                <LanguageSwitcher variant="mobile" />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
